import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppShell } from '../app/AppShell';
import {
  memoryDownloadUrl,
  useClaudeMd,
  useClaudeMdRevisions,
  useMemoryFile,
  useMemoryIndex,
  useMemoryTree,
  usePutClaudeMd,
  useRestoreClaudeMd,
  useTaskLog,
} from '../api/hooks';
import { Badge, Card, EmptyState, ErrorText, Segmented, Spinner } from '../components/ui';
import { zh } from '../i18n';
import { dirname, formatRelative } from '../lib/format';
import { ClaudeMdEditor } from './ClaudeMdEditor';
import { FileBrowser } from './FileBrowser';

/**
 * 記憶 — 索引 / 檔案 / 工作守則 / 任務紀錄 / 產出 (SPEC §10).
 *
 * Every one of these routes wakes the caller's own runner (CONTRACTS §4.5) and
 * works while the deployment is still awaiting a credential, which is exactly
 * the day-zero experience RUNBOOK §6 advertises — so nothing on this screen is
 * gated on the credential banner above it.
 */

type Tab = 'index' | 'files' | 'claude' | 'tasklog' | 'output';

const TAB_OPTIONS: readonly { value: Tab; label: string }[] = [
  { value: 'index', label: zh.memory.tabIndex },
  { value: 'files', label: zh.memory.tabFiles },
  { value: 'claude', label: zh.memory.tabClaudeMd },
  { value: 'tasklog', label: zh.memory.tabTaskLog },
  { value: 'output', label: zh.memory.tabOutput },
];

const TASK_LOG_TAIL = 400;

export function MemoryRoute(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const deepLinkedPath = params.get('path');

  const [tab, setTab] = useState<Tab>(deepLinkedPath ? 'files' : 'index');
  const [path, setPath] = useState(deepLinkedPath ? dirname(deepLinkedPath) : '');
  const [selected, setSelected] = useState<string | null>(deepLinkedPath);
  const [outputPath, setOutputPath] = useState('output');
  const [outputSelected, setOutputSelected] = useState<string | null>(null);
  const [revisionsRequested, setRevisionsRequested] = useState(false);

  // A 記憶足跡 chip deep-links here; consume the param so a later navigation
  // inside the tab does not keep snapping back to it.
  useEffect(() => {
    if (!deepLinkedPath) return;
    setTab('files');
    setSelected(deepLinkedPath);
    setPath(dirname(deepLinkedPath));
    params.delete('path');
    setParams(params, { replace: true });
  }, [deepLinkedPath, params, setParams]);

  const index = useMemoryIndex();
  const tree = useMemoryTree(path);
  const file = useMemoryFile(selected);
  const outputTree = useMemoryTree(outputPath);
  const outputFile = useMemoryFile(outputSelected);
  const claudeMd = useClaudeMd();
  const putClaudeMd = usePutClaudeMd();
  const revisions = useClaudeMdRevisions(revisionsRequested);
  const restore = useRestoreClaudeMd();
  const taskLog = useTaskLog(TASK_LOG_TAIL);

  return (
    <AppShell>
      <div className="page-head">
        <h1 className="page-head__title">{zh.memory.title}</h1>
      </div>

      <Segmented
        options={TAB_OPTIONS}
        value={tab}
        onChange={setTab}
        ariaLabel={zh.memory.title}
      />

      <div style={{ marginTop: '0.75rem' }}>
        {tab === 'index' ? (
          <div className="stack">
            <h2>{zh.memory.indexTitle}</h2>
            {index.isLoading ? <Spinner /> : null}
            <ErrorText error={index.error} />
            {index.data && index.data.entries.length === 0 && index.data.orphans.length === 0 ? (
              <EmptyState title={zh.memory.indexEmpty} />
            ) : null}
            {index.data && index.data.entries.length > 0 ? (
              <Card flush>
                <ul className="list">
                  {index.data.entries.map((entry) => (
                    <li key={entry.file}>
                      <button
                        type="button"
                        className="list__row"
                        onClick={() => {
                          setTab('files');
                          setSelected(entry.file);
                          setPath(dirname(entry.file));
                        }}
                      >
                        <div className="list__main">
                          <div className="list__title">{entry.title}</div>
                          <div className="list__sub">{entry.summary}</div>
                        </div>
                        {!entry.exists ? (
                          <Badge tone="danger">{zh.memory.indexMissingFile}</Badge>
                        ) : entry.mtime ? (
                          <span className="tiny faint nowrap">
                            {zh.memory.updatedAt(formatRelative(entry.mtime))}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              </Card>
            ) : null}
            {index.data && index.data.orphans.length > 0 ? (
              <Card>
                <h3>{zh.memory.orphans}</h3>
                <p className="small muted">{zh.memory.orphansHint}</p>
                <ul className="stack stack--sm" style={{ paddingInlineStart: '1.1rem' }}>
                  {index.data.orphans.map((orphan) => (
                    <li key={orphan.file} className="small">
                      {orphan.file}
                    </li>
                  ))}
                </ul>
              </Card>
            ) : null}
          </div>
        ) : null}

        {tab === 'files' ? (
          <FileBrowser
            path={path}
            onPathChange={(next) => {
              setSelected(null);
              setPath(next);
            }}
            entries={tree.data?.entries}
            treeLoading={tree.isLoading}
            treeError={tree.error}
            selected={selected}
            onSelect={setSelected}
            file={file.data}
            fileLoading={file.isLoading}
            fileError={file.error}
            buildDownloadUrl={memoryDownloadUrl}
            rootLabel={zh.memory.breadcrumbRoot}
            emptyLabel={zh.memory.filesEmpty}
          />
        ) : null}

        {tab === 'claude' ? (
          <ClaudeMdEditor
            doc={claudeMd.data ?? null}
            loading={claudeMd.isLoading}
            saving={putClaudeMd.isPending}
            saveError={putClaudeMd.error}
            onSave={(input) => putClaudeMd.mutate(input)}
            onReload={() => {
              putClaudeMd.reset();
              void claudeMd.refetch();
            }}
            revisions={revisions.data ?? null}
            revisionsLoading={revisions.isLoading && revisionsRequested}
            onLoadRevisions={() => setRevisionsRequested(true)}
            onRestore={(id) => restore.mutate(id)}
            restoring={restore.isPending}
            restored={restore.isSuccess}
          />
        ) : null}

        {tab === 'tasklog' ? (
          <div className="stack">
            {taskLog.isLoading ? <Spinner /> : null}
            <ErrorText error={taskLog.error} />
            {taskLog.data ? (
              taskLog.data.content.trim().length === 0 ? (
                <EmptyState title={zh.memory.taskLogEmpty} />
              ) : (
                <div className="file-view">
                  {taskLog.data.truncated ? (
                    <div className="tiny faint">{zh.memory.taskLogTail(TASK_LOG_TAIL)}</div>
                  ) : null}
                  <pre className="file-view__pre">{taskLog.data.content}</pre>
                </div>
              )
            ) : null}
          </div>
        ) : null}

        {tab === 'output' ? (
          <FileBrowser
            path={outputPath}
            onPathChange={(next) => {
              setOutputSelected(null);
              setOutputPath(next || 'output');
            }}
            entries={outputTree.data?.entries}
            treeLoading={outputTree.isLoading}
            treeError={outputTree.error}
            selected={outputSelected}
            onSelect={setOutputSelected}
            file={outputFile.data}
            fileLoading={outputFile.isLoading}
            fileError={outputFile.error}
            buildDownloadUrl={memoryDownloadUrl}
            rootLabel={zh.memory.tabOutput}
            emptyLabel={zh.memory.outputEmpty}
          />
        ) : null}
      </div>
    </AppShell>
  );
}
